import { Toaster, TooltipProvider, toast } from "@medusajs/ui"
import { QueryClientProvider } from "@tanstack/react-query"
import type { PropsWithChildren } from "react"
import { useEffect, useRef } from "react"
import { HelmetProvider } from "react-helmet-async"
import { I18n, i18n } from "../components/utilities/i18n"
import { DashboardApp } from "../dashboard-app"
import useSocketDashboard from "../hooks/use-socket-dashboard"
import { ordersQueryKeys } from "../hooks/api/orders"
import { marketplaceNotificationQueryKeys } from "../hooks/api/marketplace-notification"
import { queryClient } from "../lib/query-client"
import { ExtensionProvider } from "./extension-provider"
import { I18nProvider } from "./i18n-provider"
import { ThemeProvider } from "./theme-provider"

type ProvidersProps = PropsWithChildren<{
  api: DashboardApp["api"]
}>

export const Providers = ({ api, children }: ProvidersProps) => {
  const { lastOrderEvent, lastNotificationEvent } = useSocketDashboard()
  const lastOrderIdRef = useRef<string | undefined>(undefined)
  const lastNotifIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const orderId = lastOrderEvent?.data?.id
    if (!orderId) {
      return
    }

    queryClient.invalidateQueries({
      queryKey: ordersQueryKeys.lists(),
    })

    if (lastOrderIdRef.current !== orderId) {
      lastOrderIdRef.current = orderId

      const displayId = lastOrderEvent?.data?.display_id
      const title = displayId
        ? i18n.t("notifications.realtime.newOrderWithDisplayId", {
            displayId,
          })
        : i18n.t("notifications.realtime.orderUpdated")
      toast.info(title)
    }
  }, [lastOrderEvent])

  useEffect(() => {
    const notifId = lastNotificationEvent?.id
    if (!notifId) {
      return
    }

    queryClient.invalidateQueries({
      queryKey: marketplaceNotificationQueryKeys.all,
    })

    if (lastNotifIdRef.current !== notifId) {
      lastNotifIdRef.current = notifId
      toast.info(
        lastNotificationEvent?.title ??
          i18n.t("notifications.realtime.newNotification")
      )
    }
  }, [lastNotificationEvent])

  return (
    <TooltipProvider>
      <ExtensionProvider api={api}>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <I18n />
              <I18nProvider>{children}</I18nProvider>
              <Toaster />
            </ThemeProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </ExtensionProvider>
    </TooltipProvider>
  )
}
